import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from '../services/db';
import { User, Client, TimeEntry, PayPeriod, PayPeriodStatus, WebhookPayload } from '../types';
import { ArrowLeft, Plus, Trash2, Printer, Send, Save, Loader2, Calendar, Clock, MessageSquare, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';

interface TimesheetProps {
  user: User;
  clientId: string;
  periodId: string | null;
  onDirtyChange: (dirty: boolean) => void;
  onBack: (force?: boolean) => void;
}

const Timesheet: React.FC<TimesheetProps> = ({ user, clientId, periodId, onDirtyChange, onBack }) => {
  const [client, setClient] = useState<Client | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingPreview, setIsSendingPreview] = useState(false);
  const [status, setStatus] = useState<PayPeriodStatus>(PayPeriodStatus.OPEN);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [duplicateIds, setDuplicateIds] = useState<Set<string>>(new Set());
  
  const initialLoadDone = useRef(false);

  useEffect(() => {
    const clients = db.getClients(user.id);
    const c = clients.find(c => c.id === clientId);
    if (c) {
      setClient(c);
      setHourlyRate(c.defaultRate);
      if (periodId) {
        const period = db.getPayPeriods(user.id).find(p => p.id === periodId);
        if (period) {
          setStatus(period.status);
          setHourlyRate(period.rateSnapshot);
          const periodEntries = db.getEntries(user.id).filter(e => e.payPeriodId === periodId);
          setEntries(periodEntries);
        }
      } else {
        const open = db.getOpenEntriesForClient(user.id, clientId);
        setEntries(open);
      }
      setTimeout(() => { initialLoadDone.current = true; }, 100);
    }
  }, [user.id, clientId, periodId]);

  useEffect(() => {
    const dups = new Set<string>();
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const e1 = entries[i];
        const e2 = entries[j];
        if (
          e1.date && e1.timeIn && e1.timeOut &&
          e1.date === e2.date &&
          e1.timeIn === e2.timeIn &&
          e1.timeOut === e2.timeOut
        ) {
          dups.add(e1.id);
          dups.add(e2.id);
        }
      }
    }
    setDuplicateIds(dups);

    if (initialLoadDone.current && status === PayPeriodStatus.OPEN) {
      onDirtyChange(true);
    }
  }, [entries, hourlyRate, onDirtyChange, status]);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(val);
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  };

  const formatTime12h = (timeStr: string) => {
    if (!timeStr) return '--:--';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const calculateHours = (inTime: string, outTime: string) => {
    if (!inTime || !outTime) return 0;
    const [h1, m1] = inTime.split(':').map(Number);
    const [h2, m2] = outTime.split(':').map(Number);
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff < 0) diff += 24 * 60;
    return Math.max(0, diff / 60);
  };

  const addRow = () => {
    const newEntry: TimeEntry = {
      id: `entry-${Date.now()}-${entries.length}`,
      userId: user.id, clientId, date: new Date().toISOString().split('T')[0],
      timeIn: '', timeOut: '', notes: '', billable: true,
      hoursCalculated: 0, amountCalculated: 0
    };
    setEntries([...entries, newEntry]);
  };

  const deleteRow = (id: string) => {
    const updated = entries.filter(e => e.id !== id);
    setEntries(updated);
    db.saveEntries(user.id, updated);
  };

  const updateEntry = (id: string, field: keyof TimeEntry, value: any) => {
    setEntries(entries.map(e => {
      if (e.id === id) {
        const updated = { ...e, [field]: value };
        updated.hoursCalculated = calculateHours(updated.timeIn, updated.timeOut);
        updated.amountCalculated = updated.hoursCalculated * hourlyRate;
        return updated;
      }
      return e;
    }));
  };

  const toggleNotes = (id: string) => {
    const next = new Set(expandedNotes);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedNotes(next);
  };

  const handleSaveDraft = () => {
    if (duplicateIds.size > 0) {
      alert("Please resolve the duplicate time entries for this client before saving.");
      return;
    }
    setIsSaving(true);
    try {
      db.saveEntries(user.id, entries);
      onDirtyChange(false);
      setTimeout(() => setIsSaving(false), 600);
    } catch (e) {
      alert("Error saving locally: " + (e as Error).message);
      setIsSaving(false);
    }
  };

  const handleMarkAsPaid = () => {
    if (!periodId) return;
    const allPeriods = db.getPayPeriods(user.id);
    const pIdx = allPeriods.findIndex(p => p.id === periodId);
    if (pIdx > -1) {
      const updated = { ...allPeriods[pIdx], status: PayPeriodStatus.PAID, paidAt: new Date().toISOString() };
      db.savePayPeriod(updated);
      setStatus(PayPeriodStatus.PAID);
      alert("Invoice marked as Paid!");
    }
  };

  const handleDeletePeriod = () => {
    if (!periodId) return;
    if (!window.confirm("Delete this invoice record? The time entries will be released back to 'Unbilled' status so you can edit them again.")) return;
    
    const allPeriods = db.getPayPeriods(user.id).filter(p => p.id !== periodId);
    localStorage.setItem('ts_periods', JSON.stringify(allPeriods)); 
    
    const allEntries = db.getEntries(user.id);
    const updatedEntries = allEntries.map(e => {
      if (e.payPeriodId === periodId) {
        const { payPeriodId, ...rest } = e;
        return rest;
      }
      return e;
    });
    localStorage.setItem('ts_entries', JSON.stringify(updatedEntries));
    onBack(true);
  };

  const handlePrint = () => { 
    window.print(); 
  };

  const handleSend = async (isClosing: boolean) => {
    try {
      if (entries.length === 0) return;
      if (duplicateIds.size > 0) {
        alert("Resolve duplicates first.");
        return;
      }
      const invalid = entries.some(e => !e.date || !e.timeIn || !e.timeOut);
      if (invalid) {
        alert("Missing required fields (Date/In/Out).");
        return;
      }

      if (isClosing) setIsSending(true); else setIsSendingPreview(true);

      const sorted = [...entries].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      const totalHours = entries.reduce((sum, e) => sum + e.hoursCalculated, 0);
      const totalAmount = totalHours * hourlyRate;
      
      const finalLocalStatus = isClosing 
        ? (user.autoMarkPaid ? PayPeriodStatus.PAID : PayPeriodStatus.SENT)
        : PayPeriodStatus.OPEN;

      const payload: WebhookPayload = {
        worker: { name: user.name, email: user.email },
        client: { name: client?.name || 'Unknown', email: client?.email || 'no-email' },
        payPeriod: { 
          startDate: sorted[0]?.date || new Date().toISOString(), 
          endDate: sorted[sorted.length-1]?.date || new Date().toISOString(), 
          totalHours, rate: hourlyRate, totalAmount,
          daysWorked: new Set(entries.map(e => e.date)).size,
          status: finalLocalStatus
        },
        entries: sorted,
        isPreview: !isClosing
      };

      let sendSuccessful = false;
      const targetUrl = user.webhookUrl?.trim();

      if (targetUrl) {
        const attemptSend = async (url: string, forceSimple: boolean = false): Promise<boolean> => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); 

          try {
            const fetchOptions: RequestInit = forceSimple ? {
              method: 'POST',
              mode: 'no-cors',
              headers: { 'Content-Type': 'text/plain' },
              body: JSON.stringify(payload),
              signal: controller.signal
            } : {
              method: 'POST',
              mode: 'cors',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
              signal: controller.signal
            };

            const response = await fetch(url, fetchOptions);
            clearTimeout(timeoutId);
            if (forceSimple) return true; 
            return response.ok;
          } catch (err) {
            clearTimeout(timeoutId);
            if (!forceSimple) return await attemptSend(url, true);
            return false;
          }
        };
        sendSuccessful = await attemptSend(targetUrl);
      } else if (isClosing) {
        console.warn("No Webhook URL configured, proceeding with local finalization only.");
      }

      if (isClosing) {
        const period: PayPeriod = {
          id: `pp-${Date.now()}`, userId: user.id, clientId,
          startDate: sorted[0]?.date || new Date().toISOString(), 
          endDate: sorted[sorted.length-1]?.date || new Date().toISOString(),
          rateSnapshot: hourlyRate, 
          status: finalLocalStatus,
          totalHours, totalAmount, 
          sentAt: new Date().toISOString(),
          paidAt: user.autoMarkPaid ? new Date().toISOString() : undefined
        };
        
        db.savePayPeriod(period);
        db.saveEntries(user.id, entries.map(e => ({ ...e, payPeriodId: period.id })));
        
        onDirtyChange(false); 
        setStatus(finalLocalStatus);
        
        const message = sendSuccessful 
          ? `Timesheet sent and finalized (Status: ${finalLocalStatus})!` 
          : `Finalized locally. Automation server was unreachable.`;
        
        alert(message);
        onBack(true);
      } else if (sendSuccessful) {
        alert('Preview triggered successfully!');
      } else if (targetUrl) {
        alert('Failed to trigger preview. Check your Webhook URL or Automation logs.');
      } else {
        alert('No Webhook URL configured in Settings.');
      }

    } catch (critical: any) {
      alert(`System Error: ${critical.message}`);
    } finally {
      setIsSending(false);
      setIsSendingPreview(false);
    }
  };

  const triggerPicker = (e: React.MouseEvent) => {
    const input = e.currentTarget.closest('.input-group')?.querySelector('input');
    if (input && 'showPicker' in input) {
      try { (input as any).showPicker(); } catch (err) { input.focus(); }
    } else if (input) {
      input.focus();
    }
  };

  const totalHours = useMemo(() => entries.reduce((sum, e) => sum + e.hoursCalculated, 0), [entries]);
  const totalAmount = useMemo(() => totalHours * hourlyRate, [totalHours, hourlyRate]);
  const isReadOnly = status !== PayPeriodStatus.OPEN;

  if (!client) return null;

  return (
    <div className="space-y-4 max-w-6xl mx-auto animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 no-print">
        <button 
          onClick={() => onBack(false)} 
          className="flex items-center gap-2 text-slate-400 hover:text-slate-800 transition font-black text-[10px] uppercase tracking-widest py-2 px-4 bg-white border border-slate-200 rounded-lg"
        >
          <ArrowLeft size={14} strokeWidth={3} /> Back
        </button>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {status === PayPeriodStatus.SENT && (
            <button 
              onClick={handleMarkAsPaid}
              className="flex-1 sm:flex-none bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-[11px] hover:bg-green-700 transition flex items-center justify-center gap-2 shadow-sm"
            >
              <CheckCircle size={13} /> Mark as Paid
            </button>
          )}
          {isReadOnly && (
            <button 
              onClick={handleDeletePeriod}
              className="flex-1 sm:flex-none bg-red-50 text-red-600 px-4 py-2 rounded-lg font-bold text-[11px] hover:bg-red-100 transition flex items-center justify-center gap-2"
            >
              <RotateCcw size={13} /> Delete & Reset
            </button>
          )}
          {!isReadOnly && (
            <>
              <button 
                onClick={handleSaveDraft} 
                disabled={isSaving || isSending || isSendingPreview} 
                className="flex-1 sm:flex-none bg-white border border-slate-200 px-4 py-2 rounded-lg text-slate-700 font-bold text-[11px] hover:bg-slate-50 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save
              </button>
              <button 
                onClick={() => handleSend(false)} 
                disabled={isSendingPreview || isSending || isSaving} 
                className="flex-1 sm:flex-none bg-blue-50 text-blue-600 px-4 py-2 rounded-lg font-bold text-[11px] hover:bg-blue-100 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSendingPreview ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Preview
              </button>
              <button 
                onClick={() => handleSend(true)} 
                disabled={isSending || isSendingPreview || isSaving} 
                className="flex-1 sm:flex-none bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-[11px] hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
              >
                {isSending ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Close & Send
              </button>
            </>
          )}
          <button 
            onClick={handlePrint} 
            className="flex-1 sm:flex-none bg-slate-800 text-white px-4 py-2 rounded-lg font-bold text-[11px] hover:bg-black transition flex items-center justify-center gap-2 shadow-sm"
          >
            <Printer size={13} /> Print
          </button>
        </div>
      </div>

      <div className="print-container bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-6 md:p-12">
        <div className="space-y-10">
          <div className="flex flex-col md:flex-row justify-between items-start border-b border-slate-100 pb-10 gap-8">
            <div className="space-y-8">
              <div className="flex items-center gap-2.5">
                <div className="bg-blue-600 p-1.5 rounded-lg text-white">
                  <Clock size={16} />
                </div>
                <h1 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Work Timesheet</h1>
                {status === PayPeriodStatus.PAID && (
                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1 ml-4">
                    <CheckCircle size={10} /> Fully Paid
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-12">
                <div>
                  <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-1.5">Consultant</p>
                  <h2 className="text-sm font-black text-slate-900 leading-tight">{user.name}</h2>
                  <p className="text-[11px] text-slate-500 font-medium">{user.email}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-1.5">Bill To</p>
                  <h2 className="text-sm font-black text-slate-900 leading-tight">{client.name}</h2>
                  <p className="text-[11px] text-slate-500 font-medium">{client.email || 'No email set'}</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-start md:items-end justify-between self-stretch md:text-right">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[9px] text-slate-400 uppercase font-black tracking-widest">Hourly Rate</span>
                {!isReadOnly ? (
                  <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg px-2.5 h-8">
                    <span className="text-xs text-slate-400 font-bold">$</span>
                    <input 
                      type="number" 
                      className="w-14 bg-transparent text-right font-black text-sm outline-none" 
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                ) : (
                  <span className="font-black text-sm text-slate-900">{formatCurrency(hourlyRate)}</span>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">Pay Period Total</p>
                <p className="text-4xl font-black text-blue-600 tracking-tighter leading-none">{formatCurrency(totalAmount)}</p>
                <div className="text-[10px] font-bold text-slate-500 uppercase mt-2 tracking-widest opacity-60">
                    {totalHours.toFixed(2)} hours logged
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto scrollbar-hide border border-slate-100 rounded-xl bg-slate-50/20">
            <table className="w-full text-left table-fixed min-w-[850px]">
              <thead className="bg-slate-50/50 text-slate-400 text-[9px] uppercase font-black tracking-widest border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 w-[22%]">Work Date</th>
                  <th className="px-6 py-4 w-[20%] text-center">In</th>
                  <th className="px-6 py-4 w-[20%] text-center">Out</th>
                  <th className="px-6 py-4 w-[8%] text-center">Hrs</th>
                  <th className="px-6 py-4 w-[15%] text-right pr-4">Total</th>
                  {!isReadOnly && <th className="px-6 py-4 w-[15%] no-print text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-300 font-bold italic text-sm">No entries logged.</td></tr>
                ) : (
                  entries.map((entry) => (
                    <React.Fragment key={entry.id}>
                      <tr className={`group transition-colors ${duplicateIds.has(entry.id) ? 'bg-red-50/60' : 'hover:bg-white'}`}>
                        <td className="px-6 py-5">
                          {!isReadOnly ? (
                            <div className="input-group">
                              <input type="date" value={entry.date} onChange={e => updateEntry(entry.id, 'date', e.target.value)} className={duplicateIds.has(entry.id) ? '!border-red-300 !bg-red-50' : ''} />
                              <div className="picker-trigger" onClick={triggerPicker}></div>
                              <div className="picker-icon"><Calendar size={13} /></div>
                            </div>
                          ) : <span className="text-sm font-bold text-slate-900">{formatDateDisplay(entry.date)}</span>}
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className="flex justify-center">
                            {!isReadOnly ? (
                              <div className="input-group !w-36">
                                <input type="time" className={`text-center ${duplicateIds.has(entry.id) ? '!border-red-300 !bg-red-50' : ''}`} value={entry.timeIn} onChange={e => updateEntry(entry.id, 'timeIn', e.target.value)} />
                                <div className="picker-trigger" onClick={triggerPicker}></div>
                                <div className="picker-icon"><Clock size={13} /></div>
                              </div>
                            ) : <span className="text-sm font-semibold text-slate-800">{formatTime12h(entry.timeIn)}</span>}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className="flex justify-center">
                            {!isReadOnly ? (
                              <div className="input-group !w-36">
                                <input type="time" className={`text-center ${duplicateIds.has(entry.id) ? '!border-red-300 !bg-red-50' : ''}`} value={entry.timeOut} onChange={e => updateEntry(entry.id, 'timeOut', e.target.value)} />
                                <div className="picker-trigger" onClick={triggerPicker}></div>
                                <div className="picker-icon"><Clock size={13} /></div>
                              </div>
                            ) : <span className="text-sm font-semibold text-slate-800">{formatTime12h(entry.timeOut)}</span>}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="font-bold text-slate-900 text-sm">{entry.hoursCalculated.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-5 text-right pr-4">
                          <span className="font-black text-blue-600 text-sm">{formatCurrency(entry.amountCalculated)}</span>
                        </td>
                        {!isReadOnly && (
                          <td className="px-6 py-5 text-right no-print">
                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => toggleNotes(entry.id)} 
                                className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 ${expandedNotes.has(entry.id) ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                title="Edit Notes"
                              >
                                <MessageSquare size={14} />
                              </button>
                              <button 
                                onClick={() => deleteRow(entry.id)} 
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Entry"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {(expandedNotes.has(entry.id) || (isReadOnly && entry.notes)) && (
                        <tr className={duplicateIds.has(entry.id) ? 'bg-red-50/40' : 'bg-slate-50/30'}>
                          <td colSpan={isReadOnly ? 5 : 6} className="px-6 pb-5 pt-1">
                            <div className="flex items-center gap-2.5 px-4 py-3 bg-white border border-slate-100 rounded-xl shadow-sm">
                              <MessageSquare size={14} className="text-slate-300 shrink-0" />
                              {!isReadOnly ? (
                                <input 
                                  type="text" 
                                  className="w-full bg-transparent text-sm font-medium text-slate-600 outline-none placeholder:text-slate-300" 
                                  placeholder="Notes..."
                                  value={entry.notes}
                                  onChange={e => updateEntry(entry.id, 'notes', e.target.value)}
                                />
                              ) : <p className="text-sm text-slate-600 font-medium">{entry.notes || 'No notes.'}</p>}
                            </div>
                          </td>
                        </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
              </tbody>
            </table>
          </div>

          {!isReadOnly && (
            <div className="no-print space-y-4">
              <button 
                onClick={addRow} 
                className="w-full py-5 bg-white border-2 border-dashed border-slate-200 rounded-xl text-slate-400 font-bold text-xs hover:bg-white hover:border-blue-400 hover:text-blue-600 transition-all flex items-center justify-center gap-2 active:scale-[0.995] shadow-sm hover:shadow-md"
              >
                <Plus size={16} /> Add Work Entry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Timesheet;